import {EventEmitter} from "nate-react-api-helpers";
import {ElevationApp} from "../../ElevationApp";
import {FrmShape} from "../../drawfrm/FrmShape";
import {FrmVar} from "../../drawfrm/FrmVar";
import {DimensionKind} from "../../drawfrm/FrmShapeDimension";
import {MdFrameProfileDetail} from "../../model/frame/MdFrameProfileDetail";
import {ChildView} from "../../shared-2d/ChildView";
import {getProfileObj, TProfile} from "./Helpers";

// Which profile-cut vars does the current profile's cross-section actually use?
// Build the profile drawing off-screen against cloned vars and record every
// dimension link. The drawing code is the ground truth — e.g. the Kerf profile
// dimensions drywallHookDrop, which no name-based profile→var mapping would guess.
// Returns the matching Properties keys (see shared-2d/Properties.tsx), or null
// when the profile has no drawing (unsupported) — treat null as "unknown".
export function referencedProfileKeys(app: ElevationApp): Set<string> | null {
    const src = app.frameSpec;

    const jambFace = new FrmVar("profileRefs.jambFace", src.jambFace.get());
    const jambDepth = new FrmVar("profileRefs.jambDepth", src.jambDepth.get());
    const detail = new MdFrameProfileDetail(jambFace);

    const vars: [clone: FrmVar, live: FrmVar, key: string][] = [
        [detail.doorThickness, src.doorFrameProfile.doorThickness, "doorThickness"],
        [detail.rabbetDepth, src.doorFrameProfile.rabbetDepth, "rabbetDepth"],
        [detail.bottomRabbetDepth, src.doorFrameProfile.bottomRabbetDepth, "bottomRabbetDepth"],
        [detail.topTab, src.doorFrameProfile.topTab, "topTab"],
        [detail.bottomTab, src.doorFrameProfile.bottomTab, "bottomTab"],
        [detail.doubleEgressLip, src.doorFrameProfile.doubleEgressLip, "doubleEgressLip"],
        [detail.doubleEgressStep1, src.doorFrameProfile.doubleEgressStep1, "doubleEgressStep1"],
        [detail.doubleEgressStep2, src.doorFrameProfile.doubleEgressStep2, "doubleEgressStep2"],
        [detail.doubleEgressSmallFace, src.doorFrameProfile.doubleEgressSmallFace, "doubleEgressSmallFace"],
        [detail.drywallLip, src.doorFrameProfile.drywallLip, "drywallLip"],
        [detail.drywallHookDrop, src.doorFrameProfile.drywallHookDrop, "drywallHookDrop"],
        [detail.kerfDepth, src.doorFrameProfile.kerfDepth, "kerfDepth"],
        [detail.expFace, src.doorFrameProfile.expFace, "expFace"],
        [jambDepth, src.jambDepth, "jambDepth"],
        [jambFace, src.jambFace, "jambFace"],
    ];

    // seed the clones with the live values so the throwaway build solves the same
    // geometry the real scene would
    for(const [clone, live] of vars) {
        if(!clone.isCalculated()) clone.setInitial(live.get());
    }

    // dimensionMode=false so the throwaway shape skips dimension rendering
    const dimensionMode = new EventEmitter<DimensionKind | false>();
    dimensionMode.emit(false);
    const view = {dimensionMode} as unknown as ChildView;

    let obj: TProfile | undefined;
    let recorded: Set<FrmVar>;
    try {
        recorded = FrmShape.recordDimensionVars(() => {
            obj = getProfileObj({
                elev: app,
                view,
                side: "left",
                direction: "in",
                detail,
                jambDepth,
                jambFace,
            });
        });
    } catch(e) {
        return null;
    } finally {
        obj?.destroy();
    }

    const out = new Set<string>();
    for(const [clone, , key] of vars) {
        if(recorded.has(clone)) out.add(key);
    }
    return out;
}
